import { api } from './index';

export interface PermissionData {
  functionId: string;
  actions: string[];
}

export interface PermissionGroupData {
  name: string;
  description?: string;
  operatorId?: string; // For SUPERADMIN to specify which operator owns this permission group
  permissions: PermissionData[];
}

export interface PermissionGroup {
  id: string;
  name: string;
  description?: string;
  operatorId?: string;
  operator?: {
    id: string;
    name: string;
    email: string;
  };
  permissions: {
    id: string;
    functionId: string;
    actions: string[];
    function: {
      id: string;
      name: string;
      code: string;
      route: string;
      icon?: string;
      menuOrder: number;
      isActive: boolean;
    };
  }[];
  users: {
    id: string;
    name: string;
    email: string;
  }[];
  _count: {
    users: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface SystemFunction {
  id: string;
  name: string;
  code: string;
  route: string;
  icon?: string;
  menuOrder: number;
  isActive: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ActionOption {
  value: string;
  label: string;
}

export interface PermissionGroupImportResult {
  message: string;
  created: number;
  updated: number;
  skipped: number;
  warnings: string[];
}

export const permissionGroupsApi = {
  // Get all permission groups
  async getAll(operatorId?: string): Promise<PermissionGroup[]> {
    const params = new URLSearchParams();
    if (operatorId) {
      params.append('operatorId', operatorId);
    }
    const response = await api.get(`/permission-groups?${params.toString()}`);
    return response.data;
  },

  // Get permission group by ID
  async getById(id: string): Promise<PermissionGroup> {
    const response = await api.get(`/permission-groups/${id}`);
    return response.data;
  },

  // Create new permission group
  async create(data: PermissionGroupData): Promise<PermissionGroup> {
    const response = await api.post('/permission-groups', data);
    return response.data;
  },

  // Update permission group
  async update(id: string, data: Partial<PermissionGroupData>): Promise<PermissionGroup> {
    const response = await api.patch(`/permission-groups/${id}`, data);
    return response.data;
  },

  // Delete permission group
  async delete(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/permission-groups/${id}`);
    return response.data;
  },

  // Get available functions for permission setup
  async getAvailableFunctions(): Promise<SystemFunction[]> {
    const response = await api.get('/permission-groups/available-functions');
    return response.data;
  },

  // Get available actions
  async getAvailableActions(): Promise<ActionOption[]> {
    const response = await api.get('/permission-groups/available-actions');
    return response.data;
  },

  // Seed default permission groups
  async seedDefault(operatorId?: string): Promise<PermissionGroup[]> {
    const url = operatorId ? `/permission-groups/seed/${operatorId}` : '/permission-groups/seed';
    const response = await api.post(url);
    return response.data;
  },

  // Create comprehensive default permission groups
  async createComprehensiveDefaultGroups(operatorId?: string): Promise<PermissionGroup[]> {
    const url = operatorId ? `/permission-groups/comprehensive-seed/${operatorId}` : '/permission-groups/comprehensive-seed';
    const response = await api.post(url);
    return response.data;
  },

  async exportPermissionGroups(operatorId?: string): Promise<void> {
    const params = new URLSearchParams();
    if (operatorId) {
      params.append('operatorId', operatorId);
    }

    const response = await api.get(`/permission-groups/export?${params.toString()}`, {
      responseType: 'blob',
    });

    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename =
      filenameMatch?.[1] ||
      `permission-groups-export-${new Date().toISOString().slice(0, 10)}.json`;

    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  async importPermissionGroups(
    file: File,
    mode: 'skip' | 'replace' | 'merge',
    operatorId?: string,
  ): Promise<PermissionGroupImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);
    if (operatorId) {
      formData.append('operatorId', operatorId);
    }

    const response = await api.post('/permission-groups/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
}; 